import { useState, useEffect } from "react";
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Toolbar,
  Collapse,
} from "@mui/material";
import {
  ChevronRight,
  Description,
  Settings as SettingsIcon,
} from "@mui/icons-material";
import { Link, useLocation } from "react-router-dom";
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import SecurityIcon from '@mui/icons-material/Security';
import reportsVector from "../../assets/baseVersion/reports_vector.svg"
import securityIconImg from "../../assets/baseVersion/Security_Image.png"
import RemoveIcon from "@mui/icons-material/Remove";
import { useTheme, Theme } from "@mui/material/styles";
import dashBoardSelected from "../../assets/dashBoard_selected.svg";
import dashBoardUnselected from "../../assets/dashBoard_unselected.svg";
import onPremSelected from "../../assets/On_prem_selected.svg";
import onPremUnselected from "../../assets/on_prem_unselected.svg";
import cloudSelected from "../../assets/colud_selected.svg";
import cloudUnselected from "../../assets/cloud_unselected.svg";
import discoverySelected from "../../assets/org_selected.svg";
import discoveryUnselected from "../../assets/org_unselected.svg";
import dashBoardSelectedDark from '../../assets/dash_selected_dark.svg';
import onPremSelectedDark from '../../assets/onprem_seected_dark.svg';
import cloudSelectedDark from '../../assets/cloud_selected_dark.svg';
import discoverySelectedDark from '../../assets/org_selected_dark.svg';
import orgSelected from "../../assets/sidenav_org_selected.svg";
import orgUnselected from "../../assets/sidenav_org_light.svg";
import orgSelectedDark from '../../assets/sidenav_org_dark.svg';
import { REGION_ROUTE, ROLES_CONTENT_PROPS, ROLES_ROUTE } from "../../shared/constants/constant-data";
import useRolePermissions from "../../shared/hooks/useRolePermissions";

const drawerWidth = 250;

const listItemButtonStyles = {
  minWidth: 40,
  width: 'calc(100% - 16px)',
  margin: '0 8px',
  borderRadius: '8px',
  '&.Mui-selected': {
    backgroundColor: (theme: Theme) => theme.palette.mode === 'dark' ? '#2E323F' : 'rgba(58, 42, 218, 0.08)',
    '&:hover': {
      backgroundColor: (theme: Theme) => theme.palette.mode === 'dark' ? '#2E323F' : 'rgba(58, 42, 218, 0.12)',
    },
    '& .MuiListItemText-primary': {
      color: (theme: Theme) => theme.palette.mode === 'dark' ? '#FFFFFF' : '#3A2ADA',
    },
    '& .MuiListItemIcon-root': {
      color: (theme: Theme) => theme.palette.mode === 'dark' ? '#FFFFFF' : '#3A2ADA',
    },
  },
  '&:hover': {
    backgroundColor: (theme: Theme) => theme.palette.mode === 'dark' ? '#2E323F' : 'rgba(58, 42, 218, 0.04)',
    '& .MuiListItemText-primary': {
      color: (theme: Theme) => theme.palette.mode === 'dark' ? '#FFFFFF' : 'inherit',
    },
    '& .MuiListItemIcon-root': {
      color: (theme: Theme) => theme.palette.mode === 'dark' ? '#FFFFFF' : 'inherit',
    },
  }
};

const listItemTextStyles = {
  '& .MuiListItemText-primary': {
    fontSize: '0.875rem', // 14px
  },
};

const listItemIconStyles = {
  minWidth: 40,
  color: 'inherit',
  '&.Mui-selected': {
    color: '#1976d2',
  },
};

const betaBadgeStyles = {
  ml: 'auto',
  mr: 0.5,
  px: '7.5px',
  height: '18px',
  fontSize: '9.5px',
  fontWeight: 700,
  letterSpacing: '0.6px',
  borderRadius: '20px',
  backgroundColor: '#3A2ADA',
  color: '#FFFFFF',
  textTransform: 'uppercase' as const,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  alignSelf: 'center',
  boxShadow: '0 1px 3px rgba(58, 42, 218, 0.25)',
  userSelect: 'none',
  flexShrink: 0,
};

interface SideBarProps {
  isOpen?: boolean;
  closeSidebar: () => void;
}

const Sidebar: React.FC<SideBarProps> = ({ closeSidebar }) => {
  const location = useLocation();
  const theme = useTheme();
  const { hasPermissions, isAssetInventory } = useRolePermissions();
  const hasAdminAccess = Boolean(
    hasPermissions(ROLES_ROUTE.USER_MGNT) ||
    hasPermissions(ROLES_ROUTE.ROLE_MGNT) ||
    (hasPermissions(ROLES_ROUTE.REGION_MGNT) &&
      (hasPermissions(REGION_ROUTE.MODIFY_REGION_DATA) || hasPermissions(REGION_ROUTE.MODIFY_REGION_LEVEL)))
  );

  const isDashboardSelected = location.pathname.startsWith("/dashboard");
  const [openSubMenu, setOpenSubMenu] = useState<{ [key: string]: boolean }>({
    projects: false,
    cloud: false,
    onPremises: false,
    organization: false,
    discovery: false,
    reports: false,
    settings: false,
    security: false,
  });
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);

  const handleSubMenuItemClick = () => {
    // Close sidebar when submenu item is clicked
    closeSidebar();
    // Add any other logic needed for submenu item click (e.g., routing)
  };

  // Initialize submenu states based on current path
  useEffect(() => {
    const path = location.pathname;
    const newState = {
      projects: false,
      cloud: false,
      onPremises: false,
      organization: false,
      discovery: false,
      reports: false,
      settings: false,
      security: false,
    };

    if (path.startsWith('/on-premises/installed-software')) {
      newState.discovery = true;
    } else if (path === '/on-premises/new-newscan' || path.startsWith('/on-premises/new-newscan/')) {
      newState.settings = true;
    } else if (path.startsWith('/on-premises/scans')) {
      newState.onPremises = true;
    } else if (path === '/on-premises/new-newscan' || path.startsWith('/on-premises/new-newscan/')) {
      newState.settings = true;
    } else if (path.startsWith('/on-premises/scans')) {
      newState.onPremises = true;
    } else if (path.startsWith('/on-premises/credentials')) {
      newState.settings = true;
    } else if (path === '/compliance-dashboard' || path.startsWith('/compliance-dashboard/')) {
      newState.onPremises = true;
    } else if (path.startsWith('/on-premises')) {
      newState.onPremises = true;
    } else if (path.startsWith('/cloud')) {
      newState.cloud = true;
    } else if (path.startsWith('/discovery') || path.startsWith('/aws-discovery')) {
      newState.discovery = true;
    } else if (path === '/organization/agent' || path.startsWith('/organization/vpn') || path.startsWith('/organization/master-config') || path.startsWith('/organization/custom-ports')) {
      newState.settings = true;
    } else if (path.startsWith('/organization')) {
      if (hasAdminAccess) {
        newState.organization = true;
      }
    }

    setOpenSubMenu(newState);
  }, [location.pathname, hasAdminAccess]);

  const handleToggle = (menu: string) => {
    if (menu === "organization" && !hasAdminAccess) return;
    setOpenSubMenu((prev) => {
      const isCurrentlyOpen = prev[menu as keyof typeof prev];
      const newState = {
        projects: false,
        cloud: false,
        onPremises: false,
        organization: false,
        discovery: false,
        reports: false,
        settings: false,
        security: false,
      };
      newState[menu as keyof typeof newState] = !isCurrentlyOpen;
      return newState;
    });
  };

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        [`& .MuiDrawer-paper`]: {
          width: drawerWidth,
          boxSizing: "border-box",
          boxShadow: "2px 0 5px rgba(0, 0, 0, 0.1)",
          overflowY: 'auto',
          '&::-webkit-scrollbar': {
            width: '0px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#bdbdbd',
            borderRadius: '3px',
          },
        },
      }}
    >
      <Toolbar />
      <List sx={{ width: '100%', p: 0 }}>
        {/* Dashboard Section */}
        <ListItem disablePadding sx={{ width: '100%' }}>
          <ListItemButton
            component={Link}
            to="/dashboard"
            selected={isDashboardSelected}
            onClick={handleSubMenuItemClick}
            onMouseEnter={() => setHoveredItem("dashboard")}
            onMouseLeave={() => setHoveredItem(null)}
            sx={{
              ...listItemButtonStyles,
              backgroundColor: (theme: Theme) =>
                (isDashboardSelected || hoveredItem === "dashboard") && theme.palette.mode === 'dark'
                  ? '#2E323F'
                  : 'transparent',
            }}
          >
            <ListItemIcon>
              <img
                src={
                  theme.palette.mode === 'dark' && (isDashboardSelected || hoveredItem === 'dashboard')
                    ? dashBoardSelectedDark
                    : (isDashboardSelected || hoveredItem === 'dashboard')
                      ? dashBoardSelected
                      : dashBoardUnselected
                }
                alt="Dashboard"
                style={{ width: 24, height: 24 }}
              />
            </ListItemIcon>
            <ListItemText primary="Dashboard" sx={listItemTextStyles} />
          </ListItemButton>
        </ListItem>

        {/* Reports */}
        {hasPermissions(ROLES_CONTENT_PROPS.DOWNLOAD_REPORT) &&
          <ListItem disablePadding sx={{ width: '100%' }}>
            <ListItemButton
              sx={{ ...listItemButtonStyles }}
              component={Link}
              to="/organization/reports"
              selected={location.pathname === '/organization/reports'}
              onClick={handleSubMenuItemClick}>
              <ListItemIcon sx={{
                minWidth: 56,
                color: 'inherit',
                img: {
                  filter: (theme) => location.pathname === '/organization/reports'
                    ? (theme.palette.mode === 'dark' ? 'brightness(0) invert(1)' : 'brightness(0) saturate(100%) invert(18%) sepia(86%) saturate(4003%) hue-rotate(242deg) brightness(88%) contrast(96%)')
                    : 'none',
                  transition: 'filter 0.2s',
                },
                '.MuiListItemButton-root:hover & img': {
                  filter: (theme) => theme.palette.mode === 'dark'
                    ? 'brightness(0) invert(1)'
                    : 'brightness(0) saturate(100%) invert(18%) sepia(86%) saturate(4003%) hue-rotate(242deg) brightness(88%) contrast(96%)',
                }
              }}>
                <img src={reportsVector} alt="reports" style={{ width: 24, height: 24 }} />
              </ListItemIcon>
              <ListItemText primary="Reports" sx={listItemTextStyles} />
            </ListItemButton>
          </ListItem>
        }



        {/* On Premises */}
        {(isAssetInventory() == 0 || isAssetInventory() == 2) && (
          <>
            <ListItem disablePadding sx={{ width: '100%' }}>
              <ListItemButton
                onClick={() => handleToggle("onPremises")}
                onMouseEnter={() => setHoveredItem("onPremises")}
                onMouseLeave={() => setHoveredItem(null)}
                sx={{
                  ...listItemButtonStyles,
                  backgroundColor: (theme: Theme) =>
                    (openSubMenu.onPremises || hoveredItem === "onPremises") && theme.palette.mode === 'dark'
                      ? '#2E323F'
                      : 'transparent',
                }}
              >
                <ListItemIcon>
                  <SecurityIcon sx={{
                    color: (theme: Theme) => (openSubMenu.onPremises || hoveredItem === "onPremises") ? (theme.palette.mode === 'dark' ? '#FFFFFF' : '#3A2ADA') : '#667085'
                  }} />
                  {/* <img
                    src={
                      theme.palette.mode === 'dark' && (openSubMenu.onPremises || hoveredItem === 'onPremises')
                        ? securityIconImg
                        : (openSubMenu.onPremises || hoveredItem === 'onPremises')
                          ? securityIconImg
                          : securityIconImg
                    }
                    alt="On Premises"
                    style={{ width: 16, height: 20, marginLeft: '2px' }}
                  /> */}
                </ListItemIcon>
                <ListItemText primary="Security" sx={listItemTextStyles} />
                <ChevronRight
                  sx={{
                    color: (theme: Theme) =>
                      (openSubMenu.onPremises || hoveredItem === "onPremises")
                        ? (theme.palette.mode === 'dark' ? '#FFFFFF' : '#3A2ADA')
                        : 'inherit',
                    transform: openSubMenu.onPremises ? 'none' : 'rotate(90deg)',
                    transition: 'transform 0.2s ease-in-out'
                  }}
                />
              </ListItemButton>
            </ListItem>

          </>
        )}

        <Collapse in={openSubMenu.onPremises} timeout="auto" unmountOnExit>
          <List component="div" disablePadding sx={{
            bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(25, 118, 210, 0.02)',
            width: '100%'
          }}>
            <ListItem disablePadding sx={{ width: '100%' }}>
              <ListItemButton
                sx={{ ...listItemButtonStyles, pl: 4 }}
                component={Link}
                to="/on-premises/scans"
                selected={location.pathname === '/on-premises/scans'}
                onClick={handleSubMenuItemClick}>
                <ListItemIcon sx={listItemIconStyles}>
                  <RemoveIcon />
                </ListItemIcon>
                <ListItemText primary="Scans" sx={listItemTextStyles} />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding sx={{ width: '100%' }}>
              <ListItemButton
                sx={{ ...listItemButtonStyles, pl: 4 }}
                component={Link}
                to="/compliance-dashboard"
                selected={
                  location.pathname === '/compliance-dashboard' ||
                  location.pathname.startsWith('/compliance-dashboard/')
                }
                onClick={handleSubMenuItemClick}>
                <ListItemIcon sx={listItemIconStyles}>
                  <RemoveIcon />
                </ListItemIcon>
                <ListItemText primary="Compliance" sx={listItemTextStyles} />
              </ListItemButton>
            </ListItem>
            {/* {hasPermissions(ROLES_ROUTE.DEVICE_MGNT) && (
              <ListItem disablePadding sx={{ width: '100%' }}>
                <ListItemButton
                  sx={{ ...listItemButtonStyles, pl: 4 }}
                  component={Link}
                  to="/on-premises/assets"
                  selected={location.pathname === '/on-premises/assets'}
                  onClick={handleSubMenuItemClick}>
                  <ListItemIcon sx={listItemIconStyles}>
                    <RemoveIcon />
                  </ListItemIcon>
                  <ListItemText primary="Assets" sx={listItemTextStyles} />
                </ListItemButton>
              </ListItem>
            )} */}

            {hasPermissions(ROLES_ROUTE.DEVICE_MGNT) && (
              <ListItem disablePadding sx={{ width: '100%' }}>
                <ListItemButton
                  sx={{ ...listItemButtonStyles, pl: 4 }}
                  component={Link}
                  to="/on-premises/asset_v2"
                  selected={location.pathname === '/on-premises/asset_v2'}
                  onClick={handleSubMenuItemClick}>
                  <ListItemIcon sx={listItemIconStyles}>
                    <RemoveIcon />
                  </ListItemIcon>
                  <ListItemText primary="Asset Inventory" sx={listItemTextStyles} />
                  {/* <Box component="span" sx={betaBadgeStyles}>
                    Beta
                  </Box> */}
                </ListItemButton>
              </ListItem>
            )}

            {hasPermissions(ROLES_ROUTE.DEVICE_MGNT) && (
              <ListItem disablePadding sx={{ width: '100%' }}>
                <ListItemButton
                  sx={{ ...listItemButtonStyles, pl: 4 }}
                  component={Link}
                  to="/on-premises/configdiff"
                  selected={location.pathname === '/on-premises/configdiff'}
                  onClick={handleSubMenuItemClick}>
                  <ListItemIcon sx={listItemIconStyles}>
                    <RemoveIcon />
                  </ListItemIcon>
                  <ListItemText primary="Config Diff" sx={listItemTextStyles} />
                </ListItemButton>
              </ListItem>
            )}
            <ListItem disablePadding sx={{ width: '100%' }}>
              <ListItemButton
                sx={{ ...listItemButtonStyles, pl: 4 }}
                component={Link}
                to="/on-premises/cve"
                selected={location.pathname === '/on-premises/cve'}
                onClick={handleSubMenuItemClick}>
                <ListItemIcon sx={listItemIconStyles}>
                  <RemoveIcon />
                </ListItemIcon>
                <ListItemText primary="Vulnerability" sx={listItemTextStyles} />
              </ListItemButton>
            </ListItem>
            {hasPermissions(ROLES_ROUTE.SECURITY_INFO) && (
              <ListItem disablePadding sx={{ width: '100%' }}>
                <ListItemButton
                  sx={{ ...listItemButtonStyles, pl: 4 }}
                  component={Link}
                  to="/on-premises/security-posture"
                  selected={location.pathname === '/on-premises/security-posture'}
                  onClick={handleSubMenuItemClick}>
                  <ListItemIcon sx={listItemIconStyles}>
                    <RemoveIcon />
                  </ListItemIcon>
                  <ListItemText primary="Posture Management" sx={listItemTextStyles} />
                </ListItemButton>
              </ListItem>
            )}
            {hasPermissions(ROLES_ROUTE.ACCEPTED_RISK) && (
              <ListItem disablePadding>
                <ListItemButton sx={{ ...listItemButtonStyles, pl: 4 }} component={Link} to="/on-premises/acceptedrisk"
                  selected={location.pathname === '/on-premises/acceptedrisk'}
                  onClick={handleSubMenuItemClick}
                >
                  <ListItemIcon sx={listItemIconStyles}><RemoveIcon /></ListItemIcon>
                  <ListItemText primary="Risk Acceptance" sx={listItemTextStyles} />
                  {/* <Box component="span" sx={betaBadgeStyles}>
                    Beta
                  </Box> */}
                </ListItemButton>
              </ListItem>
            )}

            {/* {hasPermissions(ROLES_ROUTE.NEW_SCAN) && (
              <ListItem disablePadding sx={{ width: '100%' }}>
                <ListItemButton
                  sx={{ ...listItemButtonStyles, pl: 4 }}
                  component={Link}
                  to="/on-premises/newscan"
                  selected={location.pathname === '/on-premises/newscan'}
                  onClick={handleSubMenuItemClick}>
                  <ListItemIcon sx={listItemIconStyles}>
                    <RemoveIcon />
                  </ListItemIcon>
                  <ListItemText primary="New Scans " sx={listItemTextStyles} />
                </ListItemButton>
              </ListItem>
            )} */}
            {/* {hasPermissions(ROLES_ROUTE.MYSCANS) && (
              <ListItem disablePadding sx={{ width: '100%' }}>
                <ListItemButton
                  sx={{ ...listItemButtonStyles, pl: 4 }}
                  component={Link}
                  to="/on-premises/scans-1"
                  selected={location.pathname === '/on-premises/scans-1'}
                  onClick={handleSubMenuItemClick}>
                  <ListItemIcon sx={listItemIconStyles}>
                    <RemoveIcon />
                  </ListItemIcon>
                  <ListItemText primary="My Scans" sx={listItemTextStyles} />
                </ListItemButton>
              </ListItem>
            )} */}








          </List>
        </Collapse>

        {/* Cloud */}
        {(isAssetInventory() == 0 || isAssetInventory() == 1) && hasPermissions(ROLES_CONTENT_PROPS.CLOUD_SECURITY) && (
          <ListItem disablePadding sx={{ width: '100%' }}>
            <ListItemButton
              onClick={() => handleToggle("cloud")}
              onMouseEnter={() => setHoveredItem("cloud")}
              onMouseLeave={() => setHoveredItem(null)}
              sx={{
                ...listItemButtonStyles,
                backgroundColor: (theme: Theme) =>
                  (openSubMenu.cloud || hoveredItem === "cloud") && theme.palette.mode === 'dark'
                    ? '#2E323F'
                    : 'transparent',
              }}
            >
              <ListItemIcon>
                <img
                  src={
                    theme.palette.mode === 'dark' && (openSubMenu.cloud || hoveredItem === 'cloud')
                      ? cloudSelectedDark
                      : (openSubMenu.cloud || hoveredItem === 'cloud')
                        ? cloudSelected
                        : cloudUnselected
                  }
                  alt="Cloud"
                  style={{ width: 24, height: 24 }}
                />
              </ListItemIcon>
              <ListItemText primary="Cloud" sx={listItemTextStyles} />
              <ChevronRight
                sx={{
                  color: (theme: Theme) =>
                    (openSubMenu.cloud || hoveredItem === "cloud")
                      ? (theme.palette.mode === 'dark' ? '#FFFFFF' : '#3A2ADA')
                      : 'inherit',
                  transform: openSubMenu.cloud ? 'none' : 'rotate(90deg)',
                  transition: 'transform 0.2s ease-in-out'
                }}
              />
            </ListItemButton>
          </ListItem>
        )}
        <Collapse in={openSubMenu.cloud} timeout="auto" unmountOnExit>
          <List component="div" disablePadding sx={{
            bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(25, 118, 210, 0.02)',
            width: '100%'
          }}>
            {/* <ListItem disablePadding sx={{ width: '100%' }}>
              <ListItemButton
                sx={{ ...listItemButtonStyles, pl: 4 }}
                component={Link}
                to="/cloud/scans"
                selected={location.pathname === '/cloud/scans'}
                onClick={handleSubMenuItemClick}>
                <ListItemIcon sx={listItemIconStyles}>
                  <RemoveIcon />
                </ListItemIcon>
                <ListItemText primary="Scans" sx={listItemTextStyles} />
              </ListItemButton>
            </ListItem> */}
            <ListItem disablePadding sx={{ width: '100%' }}>
              <ListItemButton
                sx={{ ...listItemButtonStyles, pl: 4 }}
                component={Link}
                to="/cloud/security-posture"
                selected={location.pathname === '/cloud/security-posture'}
                onClick={handleSubMenuItemClick}>
                <ListItemIcon sx={listItemIconStyles}>
                  <RemoveIcon />
                </ListItemIcon>
                <ListItemText primary="Security Posture" sx={listItemTextStyles} />
              </ListItemButton>
            </ListItem>
            {/* <ListItem disablePadding sx={{ width: '100%' }}>
              <ListItemButton
                sx={{ ...listItemButtonStyles, pl: 4 }}
                component={Link}
                to="cloud/cloud-resources"
                selected={location.pathname === 'cloud/cloud-resources'}
                onClick={handleSubMenuItemClick}>
                <ListItemIcon sx={listItemIconStyles}>
                  <RemoveIcon />
                </ListItemIcon>
                <ListItemText primary="Resources" sx={listItemTextStyles} />
              </ListItemButton>
            </ListItem> */}
            {/* <ListItem disablePadding sx={{ width: '100%' }}>
              <ListItemButton
                sx={{ ...listItemButtonStyles, pl: 4 }}
                component={Link}
                to="cloud/rule-management"
                selected={location.pathname === 'cloud/rule-management'}
                onClick={handleSubMenuItemClick}>
                <ListItemIcon sx={listItemIconStyles}>
                  <RemoveIcon />
                </ListItemIcon>
                <ListItemText primary="Rule Management" sx={listItemTextStyles} />
              </ListItemButton>
            </ListItem> */}
          </List>
        </Collapse>

        {/* Discovery */}
        <ListItem disablePadding sx={{ width: '100%' }}>
          <ListItemButton
            onClick={() => handleToggle("discovery")}
            onMouseEnter={() => setHoveredItem("discovery")}
            onMouseLeave={() => setHoveredItem(null)}
            sx={{
              ...listItemButtonStyles,
              backgroundColor: (theme: Theme) =>
                (openSubMenu.discovery || hoveredItem === "discovery") && theme.palette.mode === 'dark'
                  ? '#2E323F'
                  : 'transparent',
            }}
          >
            <ListItemIcon>
              <img
                src={
                  theme.palette.mode === 'dark' && (openSubMenu.discovery || hoveredItem === 'discovery')
                    ? discoverySelectedDark
                    : (openSubMenu.discovery || hoveredItem === 'discovery')
                      ? discoverySelected
                      : discoveryUnselected
                }
                alt="Discovery"
                style={{ width: 24, height: 24 }}
              />
            </ListItemIcon>
            <ListItemText primary="Discovery" sx={listItemTextStyles} />
            <ChevronRight
              sx={{
                color: (theme: Theme) =>
                  (openSubMenu.discovery || hoveredItem === "discovery")
                    ? (theme.palette.mode === 'dark' ? '#FFFFFF' : '#3A2ADA')
                    : 'inherit',
                transform: openSubMenu.discovery ? 'none' : 'rotate(90deg)',
                transition: 'transform 0.2s ease-in-out'
              }}
            />
          </ListItemButton>
        </ListItem>
        <Collapse in={openSubMenu.discovery} timeout="auto" unmountOnExit>
          <List component="div" disablePadding sx={{
            bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(25, 118, 210, 0.02)',
            width: '100%'
          }}>
            <ListItem disablePadding sx={{ width: '100%' }}>
              <ListItemButton
                sx={{ ...listItemButtonStyles, pl: 4 }}
                component={Link}
                to="/discovery"
                selected={location.pathname === '/discovery'}
                onClick={handleSubMenuItemClick}>
                <ListItemIcon sx={listItemIconStyles}>
                  <RemoveIcon />
                </ListItemIcon>
                <ListItemText primary="Inventory" sx={listItemTextStyles} />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding sx={{ width: '100%' }}>
              <ListItemButton
                sx={{ ...listItemButtonStyles, pl: 4 }}
                component={Link}
                to="/aws-discovery"
                selected={location.pathname === '/aws-discovery'}
                onClick={handleSubMenuItemClick}>
                <ListItemIcon sx={listItemIconStyles}>
                  <RemoveIcon />
                </ListItemIcon>
                <ListItemText primary="AWS Inventory" sx={listItemTextStyles} />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding sx={{ width: '100%' }}>
              <ListItemButton
                sx={{ ...listItemButtonStyles, pl: 4 }}
                component={Link}
                to="/on-premises/installed-software"
                selected={location.pathname === '/on-premises/installed-software'}
                onClick={handleSubMenuItemClick}>
                <ListItemIcon sx={listItemIconStyles}>
                  <RemoveIcon />
                </ListItemIcon>
                <ListItemText primary="Installed Software" sx={listItemTextStyles} />
              </ListItemButton>
            </ListItem>
          </List>
        </Collapse>

        {/* Settings */}
        <ListItem disablePadding sx={{ width: '100%' }}>
          <ListItemButton
            onClick={() => handleToggle("settings")}
            onMouseEnter={() => setHoveredItem("settings")}
            onMouseLeave={() => setHoveredItem(null)}
            sx={{
              ...listItemButtonStyles,
              backgroundColor: (theme: Theme) =>
                (openSubMenu.settings || hoveredItem === "settings") && theme.palette.mode === 'dark'
                  ? '#2E323F'
                  : 'transparent',
            }}
          >
            <ListItemIcon>
              <SettingsIcon sx={{
                width: 24,
                height: 24,
                color: (theme: Theme) => (openSubMenu.settings || hoveredItem === "settings") ? (theme.palette.mode === 'dark' ? '#FFFFFF' : '#3A2ADA') : '#667085'
              }} />
            </ListItemIcon>
            <ListItemText primary="Settings" sx={listItemTextStyles} />
            <ChevronRight
              sx={{
                color: (theme: Theme) =>
                  (openSubMenu.settings || hoveredItem === "settings")
                    ? (theme.palette.mode === 'dark' ? '#FFFFFF' : '#3A2ADA')
                    : 'inherit',
                transform: openSubMenu.settings ? 'none' : 'rotate(90deg)',
                transition: 'transform 0.2s ease-in-out'
              }}
            />
          </ListItemButton>
        </ListItem>
        <Collapse in={openSubMenu.settings} timeout="auto" unmountOnExit>
          <List component="div" disablePadding sx={{
            bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(25, 118, 210, 0.02)',
            width: '100%'
          }}>
            {hasPermissions(ROLES_ROUTE.NEW_SCAN) && (
              <ListItem disablePadding sx={{ width: '100%' }}>
                <ListItemButton
                  sx={{ ...listItemButtonStyles, pl: 4 }}
                  component={Link}
                  to="/on-premises/new-newscan"
                  selected={location.pathname === '/on-premises/new-newscan'}
                  onClick={handleSubMenuItemClick}>
                  <ListItemIcon sx={listItemIconStyles}>
                    <RemoveIcon />
                  </ListItemIcon>
                  <ListItemText primary="New Scan" sx={listItemTextStyles} />
                </ListItemButton>
              </ListItem>
            )}
          </List>

          {hasPermissions(ROLES_CONTENT_PROPS.VIEW_AGENT) && (
            <ListItem disablePadding sx={{ width: '100%' }}>
              <ListItemButton
                sx={{ ...listItemButtonStyles, pl: 4 }}
                component={Link}
                to="/organization/agent"
                selected={location.pathname === '/organization/agent'}
                onClick={handleSubMenuItemClick}>
                <ListItemIcon sx={listItemIconStyles}>
                  <RemoveIcon />
                </ListItemIcon>
                <ListItemText primary="Agents" sx={listItemTextStyles} />
              </ListItemButton>
            </ListItem>
          )}
          {hasPermissions(ROLES_CONTENT_PROPS.VIEW_VPN) &&
            <ListItem disablePadding sx={{ width: '100%' }}>
              <ListItemButton
                sx={{ ...listItemButtonStyles, pl: 4 }}
                component={Link}
                to="/organization/vpn"
                selected={location.pathname === '/organization/vpn'}
                onClick={handleSubMenuItemClick}>
                <ListItemIcon sx={listItemIconStyles}>
                  <RemoveIcon />
                </ListItemIcon>
                <ListItemText primary="VPN" sx={listItemTextStyles} />
              </ListItemButton>
            </ListItem>
          }
          {hasPermissions(ROLES_CONTENT_PROPS.VIEW_CREDENTIALS) &&
            <ListItem disablePadding sx={{ width: '100%' }}>
              <ListItemButton
                sx={{ ...listItemButtonStyles, pl: 4 }}
                component={Link}
                to="/on-premises/credentials"
                selected={location.pathname === '/on-premises/credentials'}
                onClick={handleSubMenuItemClick}>
                <ListItemIcon sx={listItemIconStyles}>
                  <RemoveIcon />
                </ListItemIcon>
                <ListItemText primary="Credentials" sx={listItemTextStyles} />
              </ListItemButton>
            </ListItem>
          }
          {hasPermissions(ROLES_CONTENT_PROPS.VIEW_CREDENTIALS) &&
            <ListItem disablePadding sx={{ width: '100%' }}>
              <ListItemButton
                sx={{ ...listItemButtonStyles, pl: 4 }}
                component={Link}
                to="/organization/custom-ports"
                selected={location.pathname === '/organization/custom-ports'}
                onClick={handleSubMenuItemClick}>
                <ListItemIcon sx={listItemIconStyles}>
                  <RemoveIcon />
                </ListItemIcon>
                <ListItemText primary="Custom Ports" sx={listItemTextStyles} />
              </ListItemButton>
            </ListItem>
          }
          <ListItem disablePadding sx={{ width: '100%' }}>
            <ListItemButton
              sx={{ ...listItemButtonStyles, pl: 4 }}
              component={Link}
              to="/organization/master-config"
              selected={location.pathname === '/organization/master-config'}
              onClick={handleSubMenuItemClick}>
              <ListItemIcon sx={listItemIconStyles}>
                <RemoveIcon />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.75,
                      whiteSpace: "nowrap",
                    }}
                  >
                    Config Template
                    {/* <Box
                      component="span"
                      sx={{
                        ...betaBadgeStyles,
                        ml: 0,
                        mr: 0,
                        px: "5px",
                        height: "15px",
                        fontSize: "8px",
                        letterSpacing: "0.35px",
                        boxShadow: "none",
                      }}
                    >
                      Beta
                    </Box> */}
                  </Box>
                }
                sx={listItemTextStyles}
              />
            </ListItemButton>
          </ListItem>
        </Collapse>

        {/* Organization / Administration */}
        {hasAdminAccess && (
          <>
            <ListItem disablePadding sx={{ width: '100%' }}>
              <ListItemButton
                onClick={() => handleToggle("organization")}
                onMouseEnter={() => setHoveredItem("organization")}
                onMouseLeave={() => setHoveredItem(null)}
                sx={{
                  ...listItemButtonStyles,
                  backgroundColor: (theme: Theme) =>
                    (openSubMenu.organization || hoveredItem === "organization") && theme.palette.mode === 'dark'
                      ? '#2E323F'
                      : 'transparent',
                }}
              >
                <ListItemIcon>
                  <ManageAccountsIcon sx={{
                    width: 24,
                    height: 24,
                    color: (theme: Theme) => (openSubMenu.organization || hoveredItem === "organization") ? (theme.palette.mode === 'dark' ? '#FFFFFF' : '#3A2ADA') : '#667085'
                  }}></ManageAccountsIcon>
                </ListItemIcon>
                <ListItemText primary="Administration" sx={listItemTextStyles} />
                <ChevronRight
                  sx={{
                    color: (theme: Theme) =>
                      (openSubMenu.organization || hoveredItem === "organization")
                        ? (theme.palette.mode === 'dark' ? '#FFFFFF' : '#3A2ADA')
                        : 'inherit',
                    transform: openSubMenu.organization ? 'none' : 'rotate(90deg)',
                    transition: 'transform 0.2s ease-in-out'
                  }}
                />
              </ListItemButton>
            </ListItem>

            <Collapse in={openSubMenu.organization} timeout="auto" unmountOnExit>
              <List component="div" disablePadding sx={{
                bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(25, 118, 210, 0.02)',
                width: '100%'
              }}>
                {hasPermissions(ROLES_ROUTE.USER_MGNT) && (
                  <ListItem disablePadding sx={{ width: '100%' }}>
                    <ListItemButton
                      sx={{ ...listItemButtonStyles, pl: 4 }}
                      component={Link}
                      to="/organization/user-management"
                      selected={location.pathname === '/organization/user-management'}
                      onClick={handleSubMenuItemClick}>
                      <ListItemIcon sx={listItemIconStyles}>
                        <RemoveIcon />
                      </ListItemIcon>
                      <ListItemText primary="Users Management" sx={listItemTextStyles} />
                    </ListItemButton>
                  </ListItem>
                )}

                {hasPermissions(ROLES_ROUTE.ROLE_MGNT) && (
                  <ListItem disablePadding sx={{ width: '100%' }}>
                    <ListItemButton
                      sx={{ ...listItemButtonStyles, pl: 4 }}
                      component={Link}
                      to="/organization/roles"
                      selected={location.pathname === '/organization/roles'}
                      onClick={handleSubMenuItemClick}>
                      <ListItemIcon sx={listItemIconStyles}>
                        <RemoveIcon />
                      </ListItemIcon>
                      <ListItemText primary="Roles Management" sx={listItemTextStyles} />
                    </ListItemButton>
                  </ListItem>
                )}

                {hasPermissions(ROLES_ROUTE.REGION_MGNT) && (hasPermissions(REGION_ROUTE.MODIFY_REGION_DATA) || hasPermissions(REGION_ROUTE.MODIFY_REGION_LEVEL)) && (
                  <ListItem disablePadding sx={{ width: '100%' }}>
                    <ListItemButton
                      sx={{ ...listItemButtonStyles, pl: 4 }}
                      component={Link}
                      to="/organization/regions"
                      selected={location.pathname === '/organization/regions'}
                      onClick={handleSubMenuItemClick}>
                      <ListItemIcon sx={listItemIconStyles}>
                        <RemoveIcon />
                      </ListItemIcon>
                      <ListItemText primary="Regions Management" sx={listItemTextStyles} />
                    </ListItemButton>
                  </ListItem>
                )}

              </List>
            </Collapse>
          </>
        )}
      </List>
    </Drawer>
  );
};

export default Sidebar;
